import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useEffect } from 'react'
import { AuthProvider } from './contexts/AuthContext'
import { SidebarProvider } from './contexts/SidebarContext'
import { TaskPanelProvider } from './contexts/TaskPanelContext'
import { ChatPanelProvider } from './contexts/ChatPanelContext'
import { LetterWritingProvider } from './contexts/LetterWritingContext'
import { GeminiPanelProvider } from './contexts/GeminiPanelContext'
import { GeminiContextProvider } from './contexts/GeminiContextContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { Layout } from './components/Layout'
import { ProtectedRoute } from './components/ProtectedRoute'
import { Login } from './pages/Login'
import { Register } from './pages/Register'
import { VerifyEmail } from './pages/VerifyEmail'
import { TermsAndConditions } from './pages/TermsAndConditions'
import { PrivacyNotice } from './pages/PrivacyNotice'
import { PcrsResponsibilityStatement } from './pages/PcrsResponsibilityStatement'
import { Dashboard } from './pages/Dashboard'
import { isRootDomain } from './utils/tenant'
import { featureToggles } from './services/featureToggles'
import { Patients } from './pages/Patients'
import { PatientDetail } from './pages/PatientDetail'
import { PatientNew } from './pages/PatientNew'
import { PatientEdit } from './pages/PatientEdit'
import { Appointments } from './pages/Appointments'
import { Settings } from './pages/Settings'
import { Families } from './pages/Families'
import { FamilyDetail } from './pages/FamilyDetail'
import { ConsultationDetail } from './pages/ConsultationDetail'
import { ConsultationWorkspace } from './pages/ConsultationWorkspace'
import { StcSigning } from './pages/StcSigning'
import { TeamPage } from './modules/leave/TeamPage'
import { TeamHelpPage } from './modules/leave/TeamHelpPage'
import { LabResults } from './pages/LabResults'
import { LabResultDetail } from './pages/LabResultDetail'
import { BatchDocuments } from './pages/BatchDocuments'
import { CarePlanDetail } from './pages/CarePlanDetail'
import { Templates } from './pages/Templates'
import { TemplateVariablesHelp } from './pages/TemplateVariablesHelp'
import { TemplateNew } from './pages/TemplateNew'
import { Audit } from './pages/Audit'
import { TemplateEdit } from './pages/TemplateEdit'
import { TemplateDetail } from './pages/TemplateDetail'
import { Tasks } from './pages/Tasks'
import { TaskDetail } from './pages/TaskDetail'
import { FeatureTogglePanel } from './components/FeatureTogglePanel'
import { AdminTenants } from './pages/AdminTenants'
import { PatientMedicationsPrint } from './pages/PatientMedicationsPrint'
import { PatientImmunisationsPrint } from './pages/PatientImmunisationsPrint'
import { ImmunisationFeeClaim } from './pages/ImmunisationFeeClaim'
import { MockApiDevTools } from './utils/mockApiDevTools'
import { Protocols } from './pages/Protocols'
import { ProtocolNew } from './pages/ProtocolNew'
import { ProtocolDetail } from './pages/ProtocolDetail'
import { ProtocolEdit } from './pages/ProtocolEdit'
import { ProtocolCompliance } from './pages/ProtocolCompliance'
import { ResearchStudies } from './pages/ResearchStudies'
import { ResearchStudyNew } from './pages/ResearchStudyNew'
import { Reports } from './pages/Reports'
import { StcReport } from './pages/StcReport'
import { PptClaimsByPatientReport } from './pages/PptClaimsByPatientReport'
import { PatientReport } from './pages/PatientReport'
import { Referrals } from './pages/Referrals'
import { Billing } from './pages/Billing'
import { PatientBillingStatementPrint } from './pages/PatientBillingStatementPrint'

// Wrapper to handle /app/* path prefix removal
function AppPathHandler({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const navigate = useNavigate()
  
  useEffect(() => {
    // If we're on /app/* path, redirect to the path without /app prefix
    if (location.pathname.startsWith('/app')) {
      const newPath = location.pathname.replace(/^\/app/, '') || '/'
      if (newPath !== location.pathname.replace(/^\/app/, '')) {
        navigate(newPath, { replace: true })
      }
    }
  }, [location.pathname, navigate])
  
  return <>{children}</>
}

// Main app routes component (reusable)
function MainAppRoutes() {
  return (
    <ThemeProvider>
      <AppPathHandler>
        <AuthProvider>
          <SidebarProvider>
            <TaskPanelProvider>
              <ChatPanelProvider>
                <LetterWritingProvider>
                <GeminiPanelProvider>
                  <GeminiContextProvider>
          <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/privacy-notice" element={<PrivacyNotice />} />
          <Route path="/pcrs-responsibility" element={<PcrsResponsibilityStatement />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout>
                  <Navigate to="/dashboard" replace />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/patients"
            element={
              <ProtectedRoute requirePermission="patients:read">
                <Layout>
                  <Patients />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/patients/new"
            element={
              <ProtectedRoute requirePermission="patients:write">
                <Layout>
                  <PatientNew />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/patients/:id"
            element={
              <ProtectedRoute requirePermission="patients:read">
                <Layout>
                  <PatientDetail />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/patients/:id/medications/print"
            element={
              <ProtectedRoute requirePermission="patients:read">
                <Layout>
                  <PatientMedicationsPrint />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/patients/:id/immunisations/print"
            element={
              <ProtectedRoute requirePermission="patients:read">
                <Layout>
                  <PatientImmunisationsPrint />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/patients/:id/billing/statement/print"
            element={
              <ProtectedRoute requirePermission="billing-statements:read">
                <Layout>
                  <PatientBillingStatementPrint />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/patients/:id/immunisations/fee-claim"
            element={
              <ProtectedRoute requirePermission="patients:read">
                <Layout>
                  <ImmunisationFeeClaim />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/patients/:id/edit"
            element={
              <ProtectedRoute requirePermission="patients:write">
                <Layout>
                  <PatientEdit />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/appointments"
            element={
              <ProtectedRoute requirePermission="appointments:read">
                <Layout>
                  <Appointments />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing"
            element={
              <ProtectedRoute requirePermission="billing-accounts:read">
                <Layout>
                  <Billing />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/*"
            element={
              <ProtectedRoute requirePermission="settings:read">
                <Layout>
                  <Settings />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/families"
            element={
              <ProtectedRoute requirePermission="patients:read">
                <Layout>
                  <Families />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/families/:id"
            element={
              <ProtectedRoute requirePermission="patients:read">
                <Layout>
                  <FamilyDetail />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/consultations/:id"
            element={
              <ProtectedRoute requirePermission="patients:read">
                <Layout>
                  <ConsultationDetail />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/consultations/:id/workspace"
            element={
              <ProtectedRoute requirePermission="patients:write">
                <Layout>
                  <ConsultationWorkspace />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/consultations/:id/stc-signing"
            element={
              <ProtectedRoute requirePermission="patients:write">
                <Layout>
                  <StcSigning />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/stc-signing"
            element={
              <ProtectedRoute requirePermission="patients:read">
                <Layout>
                  <StcSigning />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/team"
            element={
              <ProtectedRoute>
                <Layout>
                  <TeamPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/team/help"
            element={
              <ProtectedRoute>
                <Layout>
                  <TeamHelpPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/lab-results"
            element={
              <ProtectedRoute requirePermission="lab-results:read">
                <Layout>
                  <LabResults />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/batch-documents"
            element={
              <ProtectedRoute requirePermission="patients:read">
                <Layout>
                  <BatchDocuments />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/lab-results/:id"
            element={
              <ProtectedRoute requirePermission="lab-results:read">
                <Layout>
                  <LabResultDetail />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/care-plans/:id"
            element={
              <ProtectedRoute requirePermission="patients:read">
                <Layout>
                  <CarePlanDetail />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/protocols"
            element={
              <ProtectedRoute requirePermission="protocols:read">
                <Layout>
                  <Protocols />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/protocols/new"
            element={
              <ProtectedRoute requirePermission="protocols:write">
                <Layout>
                  <ProtocolNew />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/protocols/:id/compliance"
            element={
              <ProtectedRoute requirePermission="protocols:read">
                <Layout>
                  <ProtocolCompliance />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/protocols/:id/edit"
            element={
              <ProtectedRoute requirePermission="protocols:write">
                <Layout>
                  <ProtocolEdit />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/protocols/:id"
            element={
              <ProtectedRoute requirePermission="protocols:read">
                <Layout>
                  <ProtocolDetail />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/research-studies"
            element={
              <ProtectedRoute requirePermission="research-studies:read">
                <Layout>
                  <ResearchStudies />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/research-studies/new"
            element={
              <ProtectedRoute requirePermission="research-studies:write">
                <Layout>
                  <ResearchStudyNew />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/research-studies/:id"
            element={
              <ProtectedRoute requirePermission="research-studies:read">
                <Layout>
                  <ResearchStudies />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/templates"
            element={
              <ProtectedRoute requirePermission="templates:read">
                <Layout>
                  <Templates />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/templates/help"
            element={
              <ProtectedRoute requirePermission="templates:read">
                <Layout>
                  <TemplateVariablesHelp />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute requirePermission="patients:read">
                <Layout>
                  <Reports />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/stc-status"
            element={
              <ProtectedRoute requirePermission="patients:read">
                <Layout>
                  <StcReport />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/ppt-claims-by-patient"
            element={
              <ProtectedRoute requirePermission="patients:read">
                <Layout>
                  <PptClaimsByPatientReport />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/referrals"
            element={
              <ProtectedRoute requirePermission="patients:read">
                <Layout>
                  <Referrals />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/referrals/new"
            element={
              <ProtectedRoute requirePermission="patients:write">
                <Layout>
                  <Referrals />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/patients/:id/report"
            element={
              <ProtectedRoute requirePermission="patients:read">
                <Layout>
                  <PatientReport />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/audit"
            element={
              <ProtectedRoute requirePermission="audit:read">
                <Layout>
                  <Audit />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/templates/new"
            element={
              <ProtectedRoute requirePermission="templates:write">
                <Layout>
                  <TemplateNew />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/templates/:id"
            element={
              <ProtectedRoute requirePermission="templates:read">
                <Layout>
                  <TemplateDetail />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/templates/:id/edit"
            element={
              <ProtectedRoute requirePermission="templates:write">
                <Layout>
                  <TemplateEdit />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tasks"
            element={
              <ProtectedRoute requirePermission="tasks:read">
                <Layout>
                  <Tasks />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tasks/:id"
            element={
              <ProtectedRoute requirePermission="tasks:read">
                <Layout>
                  <TaskDetail />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/tenants"
            element={
              <ProtectedRoute requirePermission="*">
                <Layout>
                  <AdminTenants />
                </Layout>
              </ProtectedRoute>
            }
          />
          {/* Catch-all route for unknown paths */}
          <Route
            path="*"
            element={
              <ProtectedRoute>
                <Layout>
                  <Navigate to="/dashboard" replace />
                </Layout>
              </ProtectedRoute>
            }
          />
          </Routes>
                  </GeminiContextProvider>
                </GeminiPanelProvider>
                </LetterWritingProvider>
              </ChatPanelProvider>
            </TaskPanelProvider>
          </SidebarProvider>
        </AuthProvider>
      </AppPathHandler>
    </ThemeProvider>
  )
}

function App() {
  // Check feature toggles
  const rootDomainRegistrationEnabled = featureToggles.get('rootDomainRegistration')
  const multiTenancyEnabled = featureToggles.get('multiTenancy')
  const appDevRoutesEnabled = featureToggles.get('appDevRoutes')
  
  // Check if we're on root domain (registration) or subdomain (main app)
  const onRootDomain = isRootDomain()
  
  // In development, allow accessing the app via /app/* routes even on root domain
  const isDevelopment = import.meta.env.DEV || window.location.hostname === 'localhost'

  // If multi-tenancy is disabled, always show main app
  if (!multiTenancyEnabled) {
    return (
      <>
        <MockApiDevTools />
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <MainAppRoutes />
        </BrowserRouter>
        <FeatureTogglePanel />
      </>
    )
  }

  // Root domain: Show registration/landing pages if enabled, otherwise show main app
  if (onRootDomain) {
    // If root domain registration is disabled, show main app
    if (!rootDomainRegistrationEnabled) {
      return (
        <>
          <MockApiDevTools />
          <BrowserRouter
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <MainAppRoutes />
          </BrowserRouter>
          <FeatureTogglePanel />
        </>
      )
    }
    
    return (
      <>
        <MockApiDevTools />
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <Routes>
            {/* Development: Allow accessing main app via /app/* routes */}
            {isDevelopment && appDevRoutesEnabled && (
              <Route path="/app/*" element={<MainAppRoutes />} />
            )}
            <Route path="/" element={<Register />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/privacy-notice" element={<PrivacyNotice />} />
            <Route path="/pcrs-responsibility" element={<PcrsResponsibilityStatement />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <FeatureTogglePanel />
      </>
    )
  }

  // Subdomain: Show main application
  return (
    <>
      <MockApiDevTools />
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <MainAppRoutes />
      </BrowserRouter>
      <FeatureTogglePanel />
    </>
  )
}

export default App
